import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import axiosRetry, { exponentialDelay } from 'axios-retry';

import { addSecHeader } from './add-sec-header';
import {
  apiErrorLogger,
  apiSuccessLogger,
  logErrorInDevMode,
  logRetryInDevMode,
} from './api-logger';
import { getBaseUrl } from './get-base-url';
import { handleProfileError } from './handle-profile-error';
import { refreshToken } from './refresh-token';

const RETRY_ATTEMPTS_COUNT = 5;

export class Api {
  private api: AxiosInstance;

  constructor() {
    const config: AxiosRequestConfig = {
      baseURL: getBaseUrl(),
    };

    this.api = axios.create(config);

    // Adding request interceptors
    this.api.interceptors.request.use(addSecHeader);

    // Adding response interceptors
    this.api.interceptors.response.use(undefined, handleProfileError);
    this.api.interceptors.response.use(undefined, refreshToken);

    // Configuring retry logic
    axiosRetry(this.api, {
      retries: RETRY_ATTEMPTS_COUNT,
      retryDelay: (retryCount, error) => exponentialDelay(retryCount, error, 1000),
      retryCondition(error) {
        if (
          error.config?.url?.includes('v1/user/profile') &&
          error.response?.status === 500
        ) {
          return false; // Skip retries for user profile on 500
        }

        if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
          return true;
        }

        return [500, 501].includes(error.response?.status || 0);
      },
      onRetry: (retryCount, error) => {
        logRetryInDevMode(error, retryCount);
      },
    });

    // Final logging interceptors
    this.api.interceptors.response.use(apiSuccessLogger, (error) => {
      logErrorInDevMode(error);
      return apiErrorLogger(error);
    });
  }

  // Expose axios methods
  get instance(): AxiosInstance {
    return this.api;
  }
}
